import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import { Card, CardHeader, CardBody, Modal, Col, Row, Spinner } from "reactstrap";
import api from "../helpers/api";
import { formatCreatedDate } from "../constants/utils";
import PermissionGuard from "../components/Common/PermissionGuard";
import { useTranslation } from "react-i18next";

// Custom field types — backend CustomFieldType enum, with industry-standard labels.
const FIELD_TYPES = [
  { value: "TEXT", labelKey: "custom_fields.type_text", fallback: "Text", descKey: "custom_fields.type_text_desc", descFallback: "A short, one-line text field." },
  { value: "TEXTAREA", labelKey: "custom_fields.type_textarea", fallback: "Paragraph", descKey: "custom_fields.type_textarea_desc", descFallback: "A multi-line text area for longer notes." },
  { value: "NUMBER", labelKey: "custom_fields.type_number", fallback: "Number", descKey: "custom_fields.type_number_desc", descFallback: "A numeric value, with optional min / max / decimals." },
  { value: "DATE", labelKey: "custom_fields.type_date", fallback: "Date", descKey: "custom_fields.type_date_desc", descFallback: "A calendar date." },
  { value: "DATETIME", labelKey: "custom_fields.type_datetime", fallback: "Date & Time", descKey: "custom_fields.type_datetime_desc", descFallback: "A date together with a time." },
  { value: "SELECT", labelKey: "custom_fields.type_select", fallback: "Dropdown", descKey: "custom_fields.type_select_desc", descFallback: "Pick one value from a list you define." },
  { value: "MULTISELECT", labelKey: "custom_fields.type_multiselect", fallback: "Multi-Select", descKey: "custom_fields.type_multiselect_desc", descFallback: "Pick several values from a list you define." },
  { value: "CHECKBOX", labelKey: "custom_fields.type_checkbox", fallback: "Checkbox", descKey: "custom_fields.type_checkbox_desc", descFallback: "A single yes/no toggle (nothing to define)." },
  { value: "LINK", labelKey: "custom_fields.type_link", fallback: "Link", descKey: "custom_fields.type_link_desc", descFallback: "A web link or phone number." },
];

const OPTION_TYPES = ["SELECT", "MULTISELECT"];
const PLACEHOLDER_TYPES = ["TEXT", "TEXTAREA", "NUMBER", "LINK"];

const emptyForm = {
  id: null,
  label: "",
  fieldType: "TEXT",
  optionsText: "",
  required: false,
  displayOrder: "",
  helpText: "",
  defaultValue: "",
  placeholder: "",
  minValue: "",
  maxValue: "",
  numberPrecision: "",
  defaultChecked: false,
};

const CustomFieldsSettings = () => {
  const { t } = useTranslation();

  const currentUser = JSON.parse(localStorage.getItem("authUser") || "null");
  const organizationId = currentUser?.activeOrganizationId ?? null;

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldModal, setFieldModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  const typeLabel = (value) => {
    const type = FIELD_TYPES.find((ft) => ft.value === value);
    return type ? t(type.labelKey, type.fallback) : value;
  };

  const typeDescription = (value) => {
    const type = FIELD_TYPES.find((ft) => ft.value === value);
    return type ? t(type.descKey, type.descFallback) : "";
  };

  const reloadFields = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `custom-field/getAll?entityType=ASSET&organizationId=${organizationId}`
      );
      setFields(data.response || []);
    } catch (error) {
      console.error("Error loading custom fields:", error);
      setApiError(t("custom_fields.failed_to_load", "Failed to load custom fields"));
    } finally {
      setLoading(false);
    }
  }, [organizationId, t]);

  useEffect(() => {
    reloadFields();
  }, [reloadFields]);

  const openCreate = () => {
    setForm(emptyForm);
    setErrors({});
    setApiError("");
    setFieldModal(true);
  };

  const openEdit = (field) => {
    setForm({
      id: field.id,
      label: field.label || "",
      fieldType: field.fieldType || "TEXT",
      optionsText: Array.isArray(field.options) ? field.options.join("\n") : "",
      required: !!field.required,
      displayOrder: field.displayOrder ?? "",
      helpText: field.helpText || "",
      defaultValue: field.fieldType === "CHECKBOX" ? "" : (field.defaultValue || ""),
      placeholder: field.placeholder || "",
      minValue: field.minValue ?? "",
      maxValue: field.maxValue ?? "",
      numberPrecision: field.numberPrecision ?? "",
      defaultChecked: field.fieldType === "CHECKBOX" && field.defaultValue === "true",
    });
    setErrors({});
    setApiError("");
    setFieldModal(true);
  };

  const closeModal = () => {
    setFieldModal(false);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    const newErrors = {};
    if (!form.label?.trim()) {
      newErrors.label = t("custom_fields.label_required", "Field label is required");
    } else if (form.label.length > 100) {
      newErrors.label = t("custom_fields.label_max_length", "Field label must be 100 characters or less");
    }
    const optionsArray = form.optionsText
      .split("\n")
      .map((o) => o.trim())
      .filter((o) => o.length > 0);
    const needsOptions = OPTION_TYPES.includes(form.fieldType);
    if (needsOptions && optionsArray.length === 0) {
      newErrors.options = t("custom_fields.options_required", "Add at least one option");
    }
    if (form.helpText && form.helpText.length > 500) {
      newErrors.helpText = t("shared.description_max_length", "Description must be 500 characters or less");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const isNumber = form.fieldType === "NUMBER";
      const payload = {
        organizationId,
        entityType: "ASSET",
        label: form.label.trim(),
        fieldType: form.fieldType,
        required: !!form.required,
        helpText: form.helpText?.trim() || null,
        placeholder: PLACEHOLDER_TYPES.includes(form.fieldType) ? (form.placeholder?.trim() || null) : null,
        minValue: isNumber && form.minValue !== "" ? Number(form.minValue) : null,
        maxValue: isNumber && form.maxValue !== "" ? Number(form.maxValue) : null,
        numberPrecision: isNumber && form.numberPrecision !== "" ? Number(form.numberPrecision) : null,
      };
      if (form.fieldType === "CHECKBOX") {
        payload.defaultValue = form.defaultChecked ? "true" : null;
      } else if (form.fieldType !== "MULTISELECT") {
        payload.defaultValue = form.defaultValue?.trim() || null;
      }
      if (form.displayOrder !== "" && form.displayOrder != null) {
        payload.displayOrder = Number(form.displayOrder);
      }
      if (needsOptions) {
        payload.options = optionsArray;
      }

      if (form.id) {
        payload.id = form.id;
        await api.put("custom-field/update", payload);
      } else {
        await api.post("custom-field/create", payload);
      }

      closeModal();
      reloadFields();
    } catch (error) {
      console.error("Error saving custom field:", error);
      setApiError(error?.response?.data?.message || t("shared.error_submission", "An error occurred during submission"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (fieldToDelete?.id) {
        await api.delete(`custom-field/delete?id=${fieldToDelete.id}`);
        setDeleteModal(false);
        setFieldToDelete(null);
        reloadFields();
      }
    } catch (error) {
      console.error("Error deleting custom field:", error);
      setApiError(t("custom_fields.failed_to_delete", "Failed to delete custom field"));
    }
  };

  return (
    <div className="mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission="custom-field:create">
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
              onClick={openCreate}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" height={21} width={21}>
                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
              </svg>
              {t("custom_fields.add_asset_field", "Add Asset Custom Field")}
            </button>
          </PermissionGuard>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t("custom_fields.asset_custom_fields", "Asset Custom Fields")}</h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("custom_fields.field_name", "Field Name")}</th>
                  <th>{t("custom_fields.field_type", "Field Type")}</th>
                  <th>{t("custom_fields.required", "Required")}</th>
                  <th>{t("shared.created_date", "Created Date")}</th>
                  <th>{t("shared.edit", "Edit")}</th>
                  <th>{t("shared.delete", "Delete")}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {fields?.map((field, ind) => (
                    <motion.tr
                      key={field.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>{field.label}</td>
                      <td>{typeLabel(field.fieldType)}</td>
                      <td>
                        {field.required
                          ? t("shared.yes", "Yes")
                          : t("shared.no", "No")}
                      </td>
                      <td>{formatCreatedDate(field.createdAt) || "-"}</td>
                      <td>
                        <PermissionGuard permission="custom-field:update">
                          <span className="text-warning" role="button" onClick={() => openEdit(field)}>
                            <FeatherIcon icon="edit" />
                          </span>
                        </PermissionGuard>
                      </td>
                      <td>
                        <PermissionGuard permission="custom-field:delete">
                          <span
                            className="text-danger"
                            role="button"
                            onClick={() => {
                              setFieldToDelete(field);
                              setDeleteModal(true);
                            }}
                          >
                            <FeatherIcon icon="trash-2" />
                          </span>
                        </PermissionGuard>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {fields.length === 0 && !loading && (
            <div className="text-center py-5 text-muted">
              {t("custom_fields.no_fields_found", "No custom fields found")}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create / Edit Modal */}
      <Modal size="lg" isOpen={fieldModal} toggle={closeModal}>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header">
            <h5 className="modal-title">
              {form.id ? t("custom_fields.edit_asset_field", "Edit Asset Custom Field") : t("custom_fields.add_asset_field", "Add Asset Custom Field")}
            </h5>
            <button onClick={closeModal} type="button" className="btn-close" aria-label={t("shared.close", "Close")}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t("custom_fields.field_name", "Field Name")} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    className={`form-control ${errors.label ? "is-invalid" : ""}`}
                    placeholder={t("custom_fields.enter_field_name", "Enter field name")}
                    maxLength={100}
                  />
                  {errors.label && <small className="text-danger">{errors.label}</small>}
                </Col>
              </Row>

              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">
                  {t("custom_fields.field_type", "Field Type")} <span className="text-danger">*</span>
                </label>
                <Col sm={9}>
                  <select
                    className="form-select"
                    value={form.fieldType}
                    disabled={!!form.id}
                    onChange={(e) => setForm({ ...form, fieldType: e.target.value })}
                  >
                    {FIELD_TYPES.map((ft) => (
                      <option key={ft.value} value={ft.value}>
                        {t(ft.labelKey, ft.fallback)}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted d-block">{typeDescription(form.fieldType)}</small>
                  {form.id && (
                    <small className="text-muted d-block">
                      {t("custom_fields.type_locked", "Field type cannot be changed after creation")}
                    </small>
                  )}
                </Col>
              </Row>

              {OPTION_TYPES.includes(form.fieldType) && (
                <Row className="mb-4">
                  <label className="col-sm-3 col-form-label">
                    {t("custom_fields.options", "Options")} <span className="text-danger">*</span>
                  </label>
                  <Col sm={9}>
                    <textarea
                      value={form.optionsText}
                      onChange={(e) => setForm({ ...form, optionsText: e.target.value })}
                      className={`form-control ${errors.options ? "is-invalid" : ""}`}
                      placeholder={t("custom_fields.options_placeholder", "One option per line")}
                      rows={4}
                    />
                    {errors.options && <small className="text-danger d-block">{errors.options}</small>}
                    <small className="text-muted">{t("custom_fields.options_hint", "Enter one option per line")}</small>
                  </Col>
                </Row>
              )}

              {PLACEHOLDER_TYPES.includes(form.fieldType) && (
                <Row className="mb-4">
                  <label className="col-sm-3 col-form-label">{t("custom_fields.placeholder", "Placeholder")}</label>
                  <Col sm={9}>
                    <input
                      type="text"
                      value={form.placeholder}
                      onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
                      className="form-control"
                      placeholder={t("custom_fields.placeholder_hint", "Hint shown inside the empty field")}
                      maxLength={255}
                    />
                  </Col>
                </Row>
              )}

              {form.fieldType === "NUMBER" && (
                <Row className="mb-4">
                  <label className="col-sm-3 col-form-label">{t("custom_fields.number_constraints", "Constraints")}</label>
                  <Col sm={9}>
                    <div className="d-flex gap-2">
                      <input type="number" value={form.minValue} onChange={(e) => setForm({ ...form, minValue: e.target.value })} className="form-control" placeholder={t("custom_fields.min_value", "Min")} />
                      <input type="number" value={form.maxValue} onChange={(e) => setForm({ ...form, maxValue: e.target.value })} className="form-control" placeholder={t("custom_fields.max_value", "Max")} />
                      <input type="number" min={0} value={form.numberPrecision} onChange={(e) => setForm({ ...form, numberPrecision: e.target.value })} className="form-control" placeholder={t("custom_fields.precision", "Decimals")} />
                    </div>
                    <small className="text-muted">{t("custom_fields.number_constraints_hint", "Optional minimum, maximum, and decimal places")}</small>
                  </Col>
                </Row>
              )}

              {form.fieldType === "CHECKBOX" ? (
                <Row className="mb-4">
                  <Col sm={{ size: 9, offset: 3 }}>
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input" id="cf-default-checked" checked={form.defaultChecked} onChange={(e) => setForm({ ...form, defaultChecked: e.target.checked })} />
                      <label className="form-check-label" htmlFor="cf-default-checked">{t("custom_fields.default_checked", "Checked by default")}</label>
                    </div>
                  </Col>
                </Row>
              ) : form.fieldType !== "MULTISELECT" && (
                <Row className="mb-4">
                  <label className="col-sm-3 col-form-label">{t("custom_fields.default_value", "Default Value")}</label>
                  <Col sm={9}>
                    <input
                      type="text"
                      value={form.defaultValue}
                      onChange={(e) => setForm({ ...form, defaultValue: e.target.value })}
                      className="form-control"
                      placeholder={t("custom_fields.default_value_hint", "Pre-filled when creating a new asset")}
                    />
                  </Col>
                </Row>
              )}

              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">{t("custom_fields.display_order", "Display Order")}</label>
                <Col sm={9}>
                  <input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                    className="form-control"
                    placeholder="0"
                    min={0}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <label className="col-sm-3 col-form-label">{t("custom_fields.help_text", "Help Text")}</label>
                <Col sm={9}>
                  <textarea
                    value={form.helpText}
                    onChange={(e) => setForm({ ...form, helpText: e.target.value })}
                    className={`form-control ${errors.helpText ? "is-invalid" : ""}`}
                    placeholder={t("custom_fields.help_text_placeholder", "Optional helper text shown under the field")}
                    rows={2}
                    maxLength={500}
                  />
                  {errors.helpText && <small className="text-danger">{errors.helpText}</small>}
                </Col>
              </Row>

              <Row className="mb-4">
                <Col sm={{ size: 9, offset: 3 }}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="custom-field-required"
                      checked={form.required}
                      onChange={(e) => setForm({ ...form, required: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="custom-field-required">
                      {t("custom_fields.required_help", "Make this field required")}
                    </label>
                  </div>
                </Col>
              </Row>

              <div className="mt-4">
                {apiError && <div className="alert alert-danger">{apiError}</div>}
                <button type="submit" className="btn btn-success w-lg transition-all hover-grow pulse-on-hover" disabled={isSubmitting}>
                  {isSubmitting
                    ? t("shared.processing", "Processing...")
                    : form.id
                    ? t("shared.update", "Update")
                    : t("shared.add", "Add")}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t("shared.close", "Close")}></button>
        </div>
        <div className="modal-body">
          <p>
            {t(
              "custom_fields.delete_confirmation",
              'Are you sure you want to delete the custom field "{{name}}"? Existing values for this field will be removed.',
              { name: fieldToDelete?.label }
            )}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
            {t("shared.cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CustomFieldsSettings;
